import { Component, ElementRef, ViewChild, OnDestroy, Input, EventEmitter, Output, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { LICENSE_PLATE_REGEX, VIN_REGEX } from './constants/regex';
import { type OcrScannerConfig, DEFAULT_OCR_CONFIG } from './config/ocr-config';
import { ScannerStateService, type ScanAttempt } from './services/scanner-state.service';
import { CameraService } from './services/camera.service';
import { CropDimensions, ImageProcessorService } from './services/image-processor.service';
import { OcrEngineService } from './services/ocr-engine.service';

/**
 * OCR Scanner Component
 * 
 * A ready-to-use camera component with zoom and torch toggle functionality for
 * better text capture for automatic vehicle identification number (VIN) and
 * license plate recognition. Uses Tesseract.js with basic image preprocessing
 * under the hood with optimized defaults for automotive text recognition.
 * 
 * @usage
 * ```html
 * <!-- Basic usage with default configuration -->
 * <app-ocr-scanner (scanned)="onScan($event)"></app-ocr-scanner>
 * 
 * <!-- With form control binding -->
 * <app-ocr-scanner [control]="form.get('vin')"></app-ocr-scanner>
 * 
 * <!-- Custom configuration override -->
 * <app-ocr-scanner [config]="customConfig"></app-ocr-scanner>
 * ```
 * 
 * @services
 * - {@link CameraService}: Camera stream and torch control
 * - {@link OcrEngineService}: Text recognition via Tesseract.js
 * - {@link ImageProcessorService}: Image cropping/filtering
 * - {@link ScannerStateService}: Scan attempt history and device state
 * 
 * @configuration
 * The scanner works out-of-the-box with {@link DEFAULT_OCR_CONFIG}. Provide a 
 * {@link OcrScannerConfig} object as input only to override specific settings.
 * 
 * @example
 * // Typical custom configuration
 * customConfig: OcrScannerConfig = {
 *   scanInterval: 2000,  // Slower processing for better accuracy
 *   imageProcessing: {
 *     contrast: 130,      // Enhanced for low-light conditions
 *     brightness: 120      // Enhanced for low-light conditions
 *   }
 * };
 * 
 * @note Camera permissions are required.
 * 
 * @see {@link DEFAULT_OCR_CONFIG} for all default values
 * @see {@link OcrScannerConfig} for configuration type reference
 */
@Component({
  selector: 'app-ocr-scanner',
  templateUrl: './ocr-scanner.html',
  styleUrls: ['./ocr-scanner.scss']
})
export class OcrScannerComponent implements OnInit, OnDestroy {

  @Input() config: OcrScannerConfig = DEFAULT_OCR_CONFIG;
  @Input() control?: FormControl;

  @Output() scanned = new EventEmitter<string>();

  @ViewChild('video') private videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('overlay') private overlayRef!: ElementRef<HTMLDivElement>;

  private scanInterval: any;
  private lastZoomTime = 0;

  private readonly DEFAULT_ZOOM = 2;
  private readonly ZOOM_DEBOUNCE_MS = 300;

  readonly maxScanAttempts = 5;
  readonly zoomLevels = [2, 3];
  showCameraModal = false;
  showRawResults = false;
  currentZoom = this.DEFAULT_ZOOM;

  constructor(
    private ocrEngine: OcrEngineService,
    private cameraService: CameraService,
    private imageProcessor: ImageProcessorService,
    public scannerState: ScannerStateService
  ) { }

  ngOnInit() {
    document.addEventListener('keydown', this.handleEscape);
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.handleEscape);
    this.cleanUp();
    this.ocrEngine.terminate();
  }

  async toggleCameraModal(): Promise<void> {
    this.showCameraModal = !this.showCameraModal;
    if (this.showCameraModal) {
      this.cleanUp();
    } else {
      await this.startScan();
    }
  }

  toggleZoom(): void {
    if (this.debounceZoom()) return;
    this.currentZoom = this.currentZoom === 2 ? 3 : 2;
    this.applyZoom();
  }

  getZoomLabel(): string {
    return this.currentZoom === 2 ? 'Zoom In' : 'Zoom Out';
  }

  useScanResult(attempt: ScanAttempt) {
    const extractedText = this.extractVinOrRegNumber(attempt.text);
    if (extractedText) {
      this.setAndEmitValue(extractedText);
      this.scannerState.updateState({ scanning: false });
    }
  }

  async toggleTorch(): Promise<void> {
    const currentState = this.scannerState.currentState;
    const success = await this.cameraService.toggleTorch(!currentState.torchActive);

    if (success) {
      this.scannerState.updateState({
        torchActive: !currentState.torchActive
      });
    } else {
      this.scannerState.updateState({
        torchSupported: false,
        torchActive: false
      });
    }
  }

  async startScan(): Promise<void> {
    try {
      this.scannerState.updateState({
        scanning: true,
        status: 'Initializing...',
        attempts: [],
        currentAttempt: 0
      });

      await this.ocrEngine.initialize({
        charWhitelist: this.config.charWhitelist,
        charBlacklist: this.config.charBlacklist,
        confidenceThreshold: 99
      });

      const stream = await this.cameraService.getCameraStream({
        facingMode: 'environment',
        resolution: { width: 1920, height: 1080 }
      });

      this.scannerState.updateState({
        status: 'Ready to scan',
        torchSupported: this.cameraService.isTorchSupported()
      });

      this.videoRef.nativeElement.srcObject = stream;
      await this.videoRef.nativeElement.play();

      this.applyInitialZoom();

      this.processFrames();
    } catch (error) {
      console.error('Scan initialization failed:', error);
      this.scannerState.updateState({
        status: 'Initialization failed',
        scanning: false
      });
      this.cleanUp();
    }
  }

  private handleEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.showCameraModal) {
      this.toggleCameraModal();
    }
  }

  private applyInitialZoom() {
    const video = this.videoRef.nativeElement;
    if (video) {
      setTimeout(() => {
        this.applyZoom();
      }, 100);
    }
  }

  private applyZoom(): void {
    const video = this.videoRef.nativeElement;
    if (video) {
      video.style.transform = `scale(${this.currentZoom})`;
      video.style.transformOrigin = 'center center';
    }
  }

  private debounceZoom(): boolean {
    const now = Date.now();
    const isWithinDebounceTime = now - this.lastZoomTime < this.ZOOM_DEBOUNCE_MS;
    this.lastZoomTime = now;
    return isWithinDebounceTime;
  }

  private getExactCropArea(video: HTMLVideoElement): CropDimensions {
    const overlay = this.overlayRef.nativeElement;
    const videoRect = video.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();

    const scaleX = video.videoWidth / videoRect.width;
    const scaleY = video.videoHeight / videoRect.height;

    return {
      x: (overlayRect.left - videoRect.left) * scaleX,
      y: (overlayRect.top - videoRect.top) * scaleY,
      width: overlayRect.width * scaleX,
      height: overlayRect.height * scaleY
    };
  }

  private processFrames(): void {
    this.scanInterval = setInterval(async () => {
      const state = this.scannerState.currentState;
      if (!state.scanning || state.currentAttempt >= state.maxAttempts) {
        this.cleanUp();
        return;
      }

      try {
        const result = await this.processFrame();
        this.scannerState.addScanAttempt(result);

        const extractedText = this.extractVinOrRegNumber(result.text);
        if (extractedText) {
          this.setAndEmitValue(extractedText);
          this.showCameraModal = false;
          this.cleanUp();
        }
      } catch (error) {
        console.error('Frame processing error:', error);
        this.scannerState.updateState({
          status: `Error: ${(error as Error).message}`
        });
      }
    }, this.config.scanInterval);
  }

  private async processFrame(): Promise<Omit<ScanAttempt, 'timestamp'>> {
    const video = this.videoRef.nativeElement;
    const canvas = this.imageProcessor.createCanvas(video.videoWidth, video.videoHeight);
    const ctx = this.imageProcessor.getCanvasContext(canvas);

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const cropArea = this.getExactCropArea(video);
    const croppedCanvas = this.imageProcessor.cropImage(canvas, cropArea);
    this.imageProcessor.applyFilters(croppedCanvas, {
      grayscale: 80,
      contrast: this.config.imageProcessing?.contrast,
      brightness: this.config.imageProcessing?.brightness
    });

    const imageData = this.imageProcessor.canvasToDataURL(croppedCanvas);
    const result = await this.ocrEngine.recognize(imageData);

    return {
      text: result.text,
      confidence: result.confidence,
      image: imageData
    };
  }

  private extractVinOrRegNumber(text: string): string | null {
    const vinMatch = text.match(VIN_REGEX);
    if (vinMatch) {
      return vinMatch[0].toUpperCase().replace(/[\s-]/g, '');
    }

    const regMatch = text.match(LICENSE_PLATE_REGEX);
    if (regMatch) {
      return regMatch[0].toUpperCase().replace(/[\s-]/g, '');
    }

    return null;
  }

  private cleanUp(): void {
    if (this.scanInterval !== undefined) {
      window.clearInterval(this.scanInterval);
      this.scanInterval = undefined;
    }

    this.cameraService.stopStream();
    this.scannerState.resetState();
    this.imageProcessor.cleanupCreatedCanvases();

    if (this.videoRef?.nativeElement) {
      this.videoRef.nativeElement.style.transform = 'none';
      this.videoRef.nativeElement.srcObject = null;
    }
  }

  private setAndEmitValue(value: string): void {
    if (this.control) {
      this.control.setValue(value);
      this.control.markAsDirty();
    }
    this.scanned.emit(value);
  }
}